import { useMemo, useRef, useState } from 'react';
import { getSelectedItems, isVisible } from './flow';
import {
  Answers,
  BasketQuestionDTO,
  BasketResponseDTO,
  ModelChoices,
} from './types';

/**
 * Estado do questionário, usado pela tela de pedido e pela prévia da
 * configuração. Todos os cards ficam abertos: responder só grava a escolha,
 * sem avançar nem rolar. `flag` + `scrollTo` levam o usuário a uma pendência.
 */
export const useQuestionnaire = (flow: BasketResponseDTO) => {
  const [answers, setAnswers] = useState<Answers>({});
  const [models, setModels] = useState<ModelChoices>({});
  const [errorFor, setErrorFor] = useState<string | null>(null);
  const blockRefs = useRef<Record<string, HTMLDivElement | null>>({});

  const visible = useMemo(
    () => flow.questions.filter((q) => isVisible(q, answers)),
    [flow, answers]
  );
  const items = useMemo(
    () => getSelectedItems(visible, answers, models),
    [visible, answers, models]
  );

  /** Grava (ou, com valor vazio, apaga) a resposta da pergunta. */
  const answer = (question: BasketQuestionDTO, value: string) => {
    const next = { ...answers };
    if (value) next[question.code] = value;
    else delete next[question.code];
    setAnswers(next);
    if (errorFor === question.code) setErrorFor(null);
  };

  /** Grava o modelo escolhido no pop-up para a pergunta. */
  const setModel = (questionCode: string, productId: number) =>
    setModels({ ...models, [questionCode]: productId });

  const reset = () => {
    setAnswers({});
    setModels({});
    setErrorFor(null);
  };

  const blockRef = (code: string) => (element: HTMLDivElement | null) => {
    blockRefs.current[code] = element;
  };

  /** Rola até o card. Usar depois da troca de aba, quando ele já existe. */
  const scrollTo = (code: string) =>
    blockRefs.current[code]?.scrollIntoView({
      block: 'center',
      behavior: 'smooth',
    });

  return {
    answers,
    models,
    visible,
    items,
    errorFor,
    answer,
    setModel,
    reset,
    flag: setErrorFor,
    blockRef,
    scrollTo,
  };
};
